import React from 'react';
import { AssignedUserSelect } from '@/components/AssignedUserSelect';
import { isAssignableAssignedUser } from '@/components/assigned-user.utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Download, Filter } from 'lucide-react';
import { SearchFieldIcon } from './SearchFieldIcon';
import { OtrosTramitesColumnToggle } from './OtrosTramitesColumnToggle';
import type {
  OtroTramiteRetiroFilter,
  OtroTramiteCobroFilter,
  OtroTramiteFacturaFilter,
  OtroTramiteTipoFilter,
  UserOption,
} from '../types';

interface OtrosTramitesFiltersProps {
  search: string;
  onSearchChange: (value: string) => void;
  retiro: OtroTramiteRetiroFilter;
  onRetiroChange: (retiro: OtroTramiteRetiroFilter) => void;
  retiroOptions: ReadonlyArray<{ value: OtroTramiteRetiroFilter; label: string }>;
  cobro: OtroTramiteCobroFilter;
  onCobroChange: (cobro: OtroTramiteCobroFilter) => void;
  cobroOptions: ReadonlyArray<{ value: OtroTramiteCobroFilter; label: string }>;
  factura: OtroTramiteFacturaFilter;
  onFacturaChange: (factura: OtroTramiteFacturaFilter) => void;
  facturaOptions: ReadonlyArray<{ value: OtroTramiteFacturaFilter; label: string }>;
  tipo: OtroTramiteTipoFilter;
  onTipoChange: (tipo: OtroTramiteTipoFilter) => void;
  tipoOptions: ReadonlyArray<{ value: OtroTramiteTipoFilter; label: string }>;
  assignedUser: UserOption | null;
  onAssignedUserChange: (user: UserOption | null) => void;
  onExport?: () => void;
}

export const OtrosTramitesFilters: React.FC<OtrosTramitesFiltersProps> = ({
  search,
  onSearchChange,
  retiro,
  onRetiroChange,
  retiroOptions,
  cobro,
  onCobroChange,
  cobroOptions,
  factura,
  onFacturaChange,
  facturaOptions,
  tipo,
  onTipoChange,
  tipoOptions,
  assignedUser,
  onAssignedUserChange,
  onExport,
}) => (
  <section className="rounded-xl border border-gray-100 bg-white p-4 sm:p-6 shadow-sm">
    <div className="flex flex-col gap-5">
      <div className="relative w-full">
        <SearchFieldIcon />
        <Input
          value={search}
          onChange={(event) => onSearchChange(event.target.value)}
          placeholder="Buscar por otorgantes, ID, responsable o detalle…"
          className="w-full pl-10"
          aria-label="Buscar trámite"
        />
      </div>

      <div className="grid gap-4 md:grid-cols-3 xl:grid-cols-6">
        <div className="flex min-w-0 flex-col gap-1.5">
          <label className="text-xs text-gray-500 font-medium px-1">
            Estado de Retiro
          </label>
          <Select value={retiro} onValueChange={(value) => onRetiroChange(value as OtroTramiteRetiroFilter)}>
            <SelectTrigger className="w-full min-w-0" aria-label="Filtrar por retiro">
              <Filter className="h-4 w-4 text-gray-400" />
              <SelectValue placeholder="Todos" />
            </SelectTrigger>
            <SelectContent>
              {retiroOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex min-w-0 flex-col gap-1.5">
          <label className="text-xs text-gray-500 font-medium px-1">
            Estado de Cobro
          </label>
          <Select value={cobro} onValueChange={(value) => onCobroChange(value as OtroTramiteCobroFilter)}>
            <SelectTrigger className="w-full min-w-0" aria-label="Filtrar por cobro">
              <Filter className="h-4 w-4 text-gray-400" />
              <SelectValue placeholder="Todos" />
            </SelectTrigger>
            <SelectContent>
              {cobroOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex min-w-0 flex-col gap-1.5">
          <label className="text-xs text-gray-500 font-medium px-1">
            Factura
          </label>
          <Select value={factura} onValueChange={(value) => onFacturaChange(value as OtroTramiteFacturaFilter)}>
            <SelectTrigger className="w-full min-w-0" aria-label="Filtrar por factura">
              <Filter className="h-4 w-4 text-gray-400" />
              <SelectValue placeholder="Todos" />
            </SelectTrigger>
            <SelectContent>
              {facturaOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex min-w-0 flex-col gap-1.5">
          <label className="text-xs text-gray-500 font-medium px-1">
            Tipo de Trámite
          </label>
          <Select value={tipo} onValueChange={(value) => onTipoChange(value as OtroTramiteTipoFilter)}>
            <SelectTrigger className="w-full min-w-0" aria-label="Filtrar por tipo">
              <Filter className="h-4 w-4 text-gray-400" />
              <SelectValue placeholder="Todos los tipos" />
            </SelectTrigger>
            <SelectContent>
              {tipoOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>


        <div className="flex min-w-0 flex-col gap-1.5">
          <label className="text-xs text-gray-500 font-medium px-1">
            Usuario Asignado
          </label>
          <AssignedUserSelect
            value={assignedUser}
            onChange={onAssignedUserChange}
            placeholder="Buscar usuario asignado"
            ariaLabel="Usuario asignado"
            filterOption={isAssignableAssignedUser}
          />
        </div>

        <div className="flex min-w-0 flex-col gap-1.5 max-w-[80px]">
          <label className="text-xs text-gray-500 font-medium px-1">
            Columnas
          </label>
          <OtrosTramitesColumnToggle />
        </div>
      </div>

      <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
        {onExport && (
          <div className="flex justify-start xl:justify-end">
            <Button variant="outline" className="w-full sm:w-auto" onClick={onExport}>
              <Download className="h-4 w-4" />
              Exportar
            </Button>
          </div>
        )}
      </div>
    </div>
  </section>
);
